import { Clipboard } from "@capacitor/clipboard";
import { Trans, t } from '@lingui/macro';
import { MoreVertical, Star, Wand2, FileText, Trash2, Copy, StarOff } from "lucide-react";
import React, { useState } from "react";
import { Favorite } from "../../models/Favorite";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { UtilApp } from "../../services/Utils/UtilApp";
import { RoomRefreshEmitter } from "../rooms/RoomEvents";
import { MessageItemState } from "./MessageItem";
import { UIMessage } from "./UIMessage";
import { MemoModalEmitter } from "../rooms/right-menu/PanelMemos";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Button } from "../../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useToast } from "../../hooks/use-toast";

export const MessagePopover: React.FC<{ m: UIMessage, state: MessageItemState }> = ({ m, state }) => {
  const { toast } = useToast();
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  async function addToMemo(mode: 'create' | 'text-assistant') {
    let text = '';
    if (m.children.length > 0) {
      text = UtilApp.getThreadText(m);
    } else {
      const dateTime = UtilApp.dateStrToLocaleDateString(m.msg.created);
      const whoWrote = t`${m.msg.user.name} wrote`;
      text = `${dateTime} - ${whoWrote}:\n\n${m.msg.text}`;
    }
    MemoModalEmitter.emit(mode, text, m.msg.roomId);
  }

  async function addToFavorites() {
    await Fire.msg.saveFavorite(new Favorite(m.msg, My.user.id));
    toast({ title: t`Added to my saved messages`, duration: 1000 });
  }

  async function removeFromFavorites() {
    await Fire.msg.deleteFavorite(My.user.id, m.msg.id);
    toast({ title: t`Removed from my saved messages`, duration: 1000 });
  }

  async function deleteMessage() {
    if (m.msg) {
      try {
        await Fire.msg.deleteMessage(m.msg.id);
        My.DeletedMessages.set(m.msg.id, true);
        RoomRefreshEmitter.emit(m.msg.roomId);
        toast({ title: t`Message deleted`, duration: 1000 });
      } catch (e: any) {
        toast({
          title: e.message || t`Failed to delete message`,
          variant: 'destructive',
          duration: 3000
        });
      }
    }
    setShowDeleteAlert(false);
  }

  async function copyToClipBoard() {
    if (m.msg?.text) {
      _copyToClipBoard(m.msg?.text);
    }
  }

  async function copyAllToClipBoard() {
    const allText = UtilApp.getThreadText(m);
    _copyToClipBoard(allText);
  }

  async function _copyToClipBoard(msgText: string) {
    console.log('copying to clipboard', msgText);
    await Clipboard.write({
      string: msgText
    });
    toast({
      title: t`Copied to clipboard`,
      duration: 1500,
    });
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8 p-0" aria-label={t`Message options`}>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="z-[70]">
          {m.isLocal && <></>}
          {!m.isLocal &&
            <>
              {state == 'favorite' && (
                <DropdownMenuItem onClick={removeFromFavorites}>
                  <StarOff className="mr-2 h-4 w-4" />
                  <span><Trans>Remove from saved</Trans></span>
                </DropdownMenuItem>
              )}
              {state != 'favorite' && <>
                {m.msg.text && <>
                  <DropdownMenuItem onClick={() => addToMemo('text-assistant')}>
                    <Wand2 className="mr-2 h-4 w-4" />
                    <span><Trans>Text Assistant</Trans></span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => addToMemo('create')}>
                    <FileText className="mr-2 h-4 w-4" />
                    <span><Trans>Create a Memo</Trans></span>
                  </DropdownMenuItem>
                </>}
                <DropdownMenuItem onClick={addToFavorites}>
                  <Star className="mr-2 h-4 w-4" />
                  <span><Trans>Save message</Trans></span>
                </DropdownMenuItem>
                {m.msg?.user.id == My.user.id && (
                  <DropdownMenuItem onClick={() => setShowDeleteAlert(true)} className="text-red-600 focus:text-red-600">
                    <Trash2 className="mr-2 h-4 w-4" />
                    <span><Trans>Delete message</Trans></span>
                  </DropdownMenuItem>
                )}
              </>}
              {m.msg?.text && !UtilApp.isWorkAppWebView() && (
                <DropdownMenuItem onClick={copyToClipBoard}>
                  <Copy className="mr-2 h-4 w-4" />
                  <span><Trans>Copy text to clipboard</Trans></span>
                </DropdownMenuItem>
              )}
              {m.children?.length > 0 && (
                <DropdownMenuItem onClick={copyAllToClipBoard}>
                  <Copy className="mr-2 h-4 w-4" />
                  <span><Trans>Copy thread to clipboard</Trans></span>
                </DropdownMenuItem>
              )}
            </>
          }
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent className="z-[70]">
          {(m.replyCount > 0 || m.nonReactions().length > 0) ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle><Trans>Cannot delete this message</Trans></AlertDialogTitle>
                <AlertDialogDescription>
                  {t`This message has replies. Please delete replies first.`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogAction className="h-10">
                  <Trans>OK</Trans>
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle><Trans>Do you want to delete this message?</Trans></AlertDialogTitle>
                <AlertDialogDescription>
                  {t`This action cannot be undone.`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="mt-0 h-10"><Trans>Cancel</Trans></AlertDialogCancel>
                <AlertDialogAction onClick={deleteMessage} className="bg-red-500 hover:bg-red-600 h-10">
                  <Trans>Delete</Trans>
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default MessagePopover;
// async function flagContent() {
//   if (m.msg) {
//     present({
//       cssClass: 'report-alert',
//       header: t`Report offensive content`,
//       message: t`Would you like to report this content as offensive?`,
//       buttons: [
//         t`Cancel`,
//         {
//           text: t`Report now`, handler: async () => {
//             close();
//             await Fire.reportContent(m.msg);
//             present({
//               header: t`Thank you for your report.`,
//               buttons: [t`OK`]
//             });
//           }
//         }
//       ]
//     })
//   }
// }